import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  WorkspaceDisplayActionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../../libs/shared/core/src/models/tracking/contexts/workspaceTracking';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface WorkspacesEditorAutoRecHelpTipProps {
  onUncheckAllClick: () => void;
  onCloseHelpTip: () => void;
  rarelyViewedItemCount: number;
}

/*
 * Styles.
 */

const StyledBannerDiv = styled.div`
  display: grid;
  grid-template-areas:
    'left-icon title close-icon'
    ' . subtitle blank'
    ' . buttons blank';
  grid-column-gap: 4px;
  // Both are necessary for different browser versions.
  grid-row-gap: 4px;
  row-gap: 4px;

  padding: 12px 12px 12px 12px;
  margin: 4px 8px 16px 8px;
  border-radius: 12px;

  background-color: ${(p) => p.theme.p1BannerBackground};
  font-size: 13px;
`;

const StyledIconDiv = styled.div`
  display: grid;
  grid-area: left-icon;
  padding-right: 2px;
`;

const StyledCloseIconDiv = styled.div`
  display: grid;
  grid-area: close-icon;
  margin-right: -7px;
  margin-top: -7px;
`;

const StyledTitle = styled.div`
  display: grid;
  grid-area: title;

  padding: 2px 2px 0px 5px;
  margin-top: 1px;
  width: 82px;
  height: 16px;
  line-height: 11px;
  border-radius: 100px;

  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.p1RarelyViewedBadgeText};
  background-color: ${(p) => p.theme.p1RarelyViewedBadgeBackground};
`;

const StyledSubtitle = styled.div`
  display: grid;
  grid-area: subtitle;

  line-height: 17px;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.p1HelpTipSubtitle};
`;

const StyledButtonDiv = styled.div`
  display: grid;
  grid-area: buttons;

  margin-left: -12px;
  padding-top: 4px;
  width: fit-content;
`;

/*
 * Component.
 */

export const WorkspacesEditorAutoRecHelpTip: FC<WorkspacesEditorAutoRecHelpTipProps> = (props) => {
  const theme = useUserTheme();

  const onClose = () => {
    props.onCloseHelpTip();
    trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_SELECT, {
      action: WorkspaceDisplayActionsEnum.DISMISS,
      section: WorkspaceDisplaySectionTrackingTypesEnum.P1_REC_HELP_TIP,
    });
  };

  return (
    <StyledBannerDiv>
      <StyledIconDiv>
        <Icon name="lightbulbSmall" color={theme.p1BannerIcon} />
      </StyledIconDiv>
      <StyledTitle>
        <Trans id="workspaces-editor-autorec-title">Rarely viewed</Trans>
      </StyledTitle>
      <StyledCloseIconDiv>
        <IconButton
          iconName="closeSmall"
          label={<Trans id="auto-rec-help-tip-close">Close</Trans>}
          color={theme.p1HelpTipClose}
          hoverColor={theme.p1HelpTipCloseHover}
          backgroundHoverColor={theme.p1HelpTipCloseBackgroundHover}
          onClick={onClose}
        />
      </StyledCloseIconDiv>
      <StyledSubtitle>
        <Trans id="workspaces-editor-autorec-subtitle">
          This badge indicates the {props.rarelyViewedItemCount}{' '}
          {props.rarelyViewedItemCount > 1 ? 'items' : 'item'} you haven&apos;t viewed in the last 4 weeks.
        </Trans>
      </StyledSubtitle>
      <StyledButtonDiv>
        <Button
          onClick={props.onUncheckAllClick}
          variant={theme.p1BannerLinkVariant}
          size={VisualSizesEnum.SMALL}
        >
          <Trans id="workspaces-editor-autorec-hideall">Hide all</Trans>{' '}
          <Plural
            id="workspaces-editor-autorec-uncheckallcount"
            value={props.rarelyViewedItemCount}
            one="(1)"
            other="(#)"
          />
        </Button>
      </StyledButtonDiv>
    </StyledBannerDiv>
  );
};
